import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';
import type { GuardListItem } from '../../types';

const columns: Column<GuardListItem>[] = [
  { key: 'name', header: 'Name' },
  { key: 'external_id', header: 'External ID', render: (r) => r.external_id || '—' },
  { key: 'email', header: 'Email', render: (r) => r.email || '—' },
  { key: 'score', header: 'Score', render: (r) => (
    <span className={`font-semibold ${getScoreColorClass(getScoreColor(r.score))}`}>
      {toGGScore(r.score)}
    </span>
  )},
  { key: 'is_active', header: 'Status', render: (r) => (
    <StatusBadge label={r.is_active ? 'Active' : 'Inactive'} variant={r.is_active ? 'success' : 'neutral'} />
  )},
  { key: 'assigned_sites_count', header: 'Sites' },
];

export default function AdminGuards() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'guards', page, search],
    queryFn: () => adminApi.getGuards({ page: String(page), search: search || undefined }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Guards" description="All guards across vendors" />
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search guards..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
      <DataTable
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(r) => r.id}
        isLoading={isLoading}
        emptyTitle="No guards"
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(r) => navigate(`/admin/guards/${r.id}`)}
      />
    </>
  );
}
